import { DocumentHeader } from "../types/DocumentHeader";
import { DocumentDTO } from "../types/DocumentDTO";
import { AssignmentHistoryResponse } from "../types/DocumentOwnerHistory";
import { DocumentListItemDTO } from "../types/DocumentListItemDTO";
import { GridSortModel, GridFilterModel } from "@mui/x-data-grid-premium";
import { apiFetch } from "./apiFetch";
import { getApiErrorMessage, normalizeFetchError } from "./apiError";
import {
  DOCUMENT_SERVER_SIDE_FILTERABLE_FIELDS,
  DOCUMENT_SERVER_SIDE_SORTABLE_FIELDS,
} from "../constants/documentServerSideFields";

const API_URL = process.env.REACT_APP_API_URL;

const VALUELESS_FILTER_OPERATORS = new Set(["isEmpty", "isNotEmpty"]);

function hasUsableFilterValue(operator: string | undefined, value: unknown): boolean {
  if (operator && VALUELESS_FILTER_OPERATORS.has(operator)) {
    return true;
  }

  if (value === undefined || value === null) {
    return false;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return String(value).trim() !== "";
}

export type PaymentMethodOption = {
  value: string;
  label: string;
};

type CreateDocumentPayload = {
  vendorNo: string;
  incomingDocNo: string;
  dueDate: string;
  documentDate: string;
  salesDate?: string | null;
  currencyCode: string;
  attachmentName?: string | null;
  attachmentContent?: string | null;
  amountExclVAT: number;
  amountInclVAT: number;
  lines: {
    description: string;
    quantity: number;
    directUnitCost: number;
    vat: number;
  }[];
};

export async function getPaymentMethodOptions(
  companyId: string
): Promise<PaymentMethodOption[]> {
  const params = new URLSearchParams();
  params.set("companyId", companyId);

  const res = await apiFetch(
    `${API_URL}/documents/payment-method-options?${params.toString()}`
  );

  if (!res.ok) {
    throw new Error(
      await getApiErrorMessage(res, "Nie udało się pobrać form płatności.")
    );
  }

  const payload = await res.json();

  return Array.isArray(payload?.items) ? payload.items : [];
}


export async function getDocumentHeaders(companyId: string): Promise<DocumentHeader[]> {
  const res = await apiFetch(`${API_URL}/documents?companyId=${companyId}`);

  if (!res.ok) {
    throw new Error(
      await getApiErrorMessage(res, "Nie udało się pobrać dokumentów.")
    );
  }

  return res.json();
}


export async function getDocumentDetails(
  companyId: string,
  documentId: string
): Promise<DocumentDTO> {
  const res = await apiFetch(
    `${API_URL}/documents/${documentId}?companyId=${companyId}`

  );

  if (!res.ok) {
    throw new Error(
      await getApiErrorMessage(res, "Nie udało się pobrać szczegółów dokumentu.")
    );
  }

  return res.json();
}


export async function getDocumentListServerSide(
  companyId: string,
  pagination: { page: number; pageSize: number },
  sortModel: GridSortModel,
  filterModel: GridFilterModel
): Promise<{ items: DocumentListItemDTO[]; total: number }> {
  const params = new URLSearchParams();

  params.set("companyId", companyId);
  params.set("page", pagination.page.toString());
  params.set("pageSize", pagination.pageSize.toString());

  const sortItem = sortModel[0];

  if (sortItem?.field && DOCUMENT_SERVER_SIDE_SORTABLE_FIELDS.has(sortItem.field)) {
    params.set("sortField", sortItem.field);
    params.set("sortOrder", sortItem.sort ?? "asc");
  }

  // Filtry wysyłamy tylko dla pól, które backend/BC potrafią bezpiecznie obsłużyć
  // w trybie server-side. Dzięki temu stare zapisane filtry z localStorage nie wywołają
  // błędu OData po stronie Business Central.
  if (filterModel.items && filterModel.items.length > 0) {
    const sanitizedItems = filterModel.items
      .filter(
        (f) =>
          DOCUMENT_SERVER_SIDE_FILTERABLE_FIELDS.has(f.field) &&
          hasUsableFilterValue(f.operator, f.value)
      )
      .map((f) => ({
        field: f.field,
        operator: f.operator,
        value: f.value,
      }));

    if (sanitizedItems.length > 0) {
      params.set("filters", JSON.stringify(sanitizedItems));
    }
  }

  const res = await apiFetch(
    `${API_URL}/documents?${params.toString()}`
  );

  if (!res.ok) {
    throw new Error(
      await getApiErrorMessage(res, "Nie udało się pobrać dokumentów.")
    );
  }

  return res.json();
}


export async function approveDocument(
  companyId: string,
  documentId: string
) {
  try {
    const res = await apiFetch(
      `${API_URL}/documents/${documentId}/approve?companyId=${companyId}`,
      { method: "POST" }
    );

    if (!res.ok) {
      throw new Error(
        await getApiErrorMessage(res, "Nie udało się zatwierdzić dokumentu.")
      );
    }
  } catch (e) {
    throw normalizeFetchError(e, "Nie udało się zatwierdzić dokumentu.");
  }
}


export async function bulkApproveDocuments(
  companyId: string,
  documentIds: string[]
) {
  try {
    const res = await apiFetch(
      `${API_URL}/documents/bulk/approve?companyId=${companyId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ documentIds }),
      }
    );

    if (!res.ok) {
      throw new Error(
        await getApiErrorMessage(res, "Nie udało się zatwierdzić dokumentów.")
      );
    }

    return res.json();
  } catch (e) {
    throw normalizeFetchError(e, "Nie udało się zatwierdzić dokumentów.");
  }
}


export async function rejectDocument(
  companyId: string,
  documentId: string,
  comment: string
): Promise<void> {
  try {
    const res = await apiFetch(
      `${API_URL}/documents/${documentId}/reject?companyId=${companyId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ comment }),
      }
    );

    if (!res.ok) {
      throw new Error(
        await getApiErrorMessage(res, "Nie udało się odrzucić dokumentu.")
      );
    }
  } catch (e) {
    throw normalizeFetchError(e, "Nie udało się odrzucić dokumentu.");
  }
}


export async function bulkRejectDocuments(
  companyId: string,
  documentIds: string[],
  comment: string
) {
  try {
    const res = await apiFetch(
      `${API_URL}/documents/bulk/reject?companyId=${companyId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ documentIds, comment }),
      }
    );

    if (!res.ok) {
      throw new Error(
        await getApiErrorMessage(res, "Nie udało się odrzucić dokumentów.")
      );
    }

    return res.json();
  } catch (e) {
    throw normalizeFetchError(e, "Nie udało się odrzucić dokumentów.");
  }
}


export async function createDocument(
  companyId: string,
  payload: CreateDocumentPayload
): Promise<void> {
  try {
    const res = await apiFetch(
      `${API_URL}/documents?companyId=${companyId}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }
    );

    if (!res.ok) {
      throw new Error(
        await getApiErrorMessage(res, "Nie udało się dodać faktury.")
      );
    }
  } catch (e) {
    throw normalizeFetchError(e, "Nie udało się dodać faktury.");
  }
}

export async function addDocumentAttachment(
  companyId: string,
  documentId: string,
  payload: {
    filename: string;
    description?: string;
    fileBase64: string;
  }
): Promise<void> {
  try {
    const res = await apiFetch(
      `${API_URL}/documents/${documentId}/attachments?companyId=${companyId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }
    );

    if (!res.ok) {
      throw new Error(
        await getApiErrorMessage(res, "Nie udało się dodać załącznika.")
      );
    }
  } catch (e) {
    throw normalizeFetchError(e, "Nie udało się dodać załącznika.");
  }
}

export async function deleteDocumentAttachment(
  companyId: string,
  documentId: string,
  attachmentId: string
): Promise<void> {
  try {
    const res = await apiFetch(
      `${API_URL}/documents/${documentId}/attachments/${attachmentId}?companyId=${companyId}`,
      {
        method: "DELETE",
      }
    );

    if (!res.ok) {
      throw new Error(
        await getApiErrorMessage(res, "Nie udało się usunąć załącznika.")
      );
    }
  } catch (e) {
    throw normalizeFetchError(e, "Nie udało się usunąć załącznika.");
  }
}

// -----------------------------
// ATTACHMENT PREVIEW
// -----------------------------
export async function getDocumentAttachmentFile(
  companyId: string,
  documentId: string,
  attachmentId: string
): Promise<string> {
  try {
    const res = await apiFetch(
      `${API_URL}/documents/${documentId}/attachments/${attachmentId}/file?companyId=${companyId}`
    );

    if (!res.ok) {
      throw new Error(
        await getApiErrorMessage(res, "Nie udało się pobrać załącznika.")
      );
    }

    const data = await res.json();
    return data.base64;
  } catch (e) {
    throw normalizeFetchError(e, "Nie udało się pobrać załącznika.");
  }
}


// -----------------------------
// Document owner assignment
// -----------------------------
export async function assignDocumentOwner(
  documentId: string,
  companyId: string,
  userId: string
) {
  try {
    const res = await apiFetch(
      `${API_URL}/documents/${documentId}/assign?companyId=${companyId}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId }),
      }
    );

    if (!res.ok) {
      throw new Error(
        await getApiErrorMessage(res, "Nie udało się przypisać właściciela dokumentu.")
      );
    }

    return res.json();
  } catch (e) {
     throw normalizeFetchError(e, "Nie udało się przypisać właściciela dokumentu.");
  }
}


export async function fetchDocumentsFromKsef(companyId: string) {
  try {
    const res = await apiFetch(
      `${API_URL}/documents/ksef/download?companyId=${companyId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      }
    );

    if (!res.ok) {
      throw new Error(
        await getApiErrorMessage(res, "Nie udało się pobrać dokumentów z KSeF.")
      );
    }

    return res.json();
  } catch (e) {
    throw normalizeFetchError(e, "Nie udało się pobrać dokumentów z KSeF.");
  } 
}

export async function updateProcessedExternally(
  documentId: string,
  companyId: string,
  value: boolean
) {
  try {
    const res = await apiFetch(
      `${API_URL}/documents/${documentId}/processed-externally?companyId=${companyId}`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          processedExternally: value,
        }),
      }
    );

    if (!res.ok) {
      throw new Error(
        await getApiErrorMessage(res, "Nie udało się zaktualizować statusu dokumentu.")
      );
    }

    return res.json();    
  } catch (e) {
    throw normalizeFetchError(e, "Nie udało się zaktualizować statusu dokumentu.");
  }
}

export async function updateProcessed(
  documentId: string,
  companyId: string,
  value: boolean
) {
  try {
    const res = await apiFetch(
      `${API_URL}/documents/${documentId}/processed?companyId=${companyId}`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          processed: value,
        }),
      }
    );

    if (!res.ok) {
      throw new Error(
        await getApiErrorMessage(
          res,
          "Nie udało się zaktualizować statusu przetworzenia dokumentu."
        )
      );
    }

    return res.json();
  } catch (e) {
    throw normalizeFetchError(
      e,
      "Nie udało się zaktualizować statusu przetworzenia dokumentu."
    );
  }
}

export async function updatePaymentStatus(
  documentId: string,
  companyId: string,
  paymentStatus: string
) {
  try {
    const res = await apiFetch(
      `${API_URL}/documents/${documentId}/payment-status?companyId=${companyId}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ paymentStatus }),
      }
    );

    if (!res.ok) {
      throw new Error(
        await getApiErrorMessage(res, "Nie udało się zaktualizować statusu płatności dokumentu.")
      );
    }

    return res.json();
  } catch (e) {
    throw normalizeFetchError(e, "Nie udało się zaktualizować statusu płatności dokumentu.");
  }
}

export async function bulkUpdatePaymentStatus(
  companyId: string,
  documentIds: string[],
  paymentStatus: string
) {
  try {
      const res = await apiFetch(
      `${API_URL}/documents/bulk/payment-status?companyId=${companyId}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ documentIds, paymentStatus }),
      }
    );

    if (!res.ok) {
      throw new Error(
        await getApiErrorMessage(res, "Nie udało się zaktualizować statusu płatności dokumentów.")
      );
    }

    return res.json();
  } catch (e) {
    throw normalizeFetchError(e, "Nie udało się zaktualizować statusu płatności dokumentów.");
  } 
}

export async function updateDueDate(
  documentId: string,
  companyId: string,
  newDueDate: string // "YYYY-MM-DD"
) {
  try {
    const res = await apiFetch(
      `${API_URL}/documents/${documentId}/due-date?companyId=${companyId}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ newDueDate }),
      }
    );

    if (!res.ok) {
      throw new Error(
        await getApiErrorMessage(res, "Nie udało się zaktualizować terminu płatności dokumentu.")
      );
    }

    return res.json();
  } catch (e) {
     throw normalizeFetchError(e, "Nie udało się zaktualizować terminu płatności dokumentu.");
  }
}

export async function updatePaymentMethod(
  documentId: string,
  companyId: string,
  paymentMethod: string
) {
  try {
    const params = new URLSearchParams();
    params.set("companyId", companyId);

    const res = await apiFetch(
      `${API_URL}/documents/${documentId}/payment-method?${params.toString()}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ paymentMethod }),
      }
    );

    if (!res.ok) {
      throw new Error(
        await getApiErrorMessage(res, "Nie udało się zaktualizować formy płatności dokumentu.")
      );
    }

    return res.json();
  } catch (e) {
    throw normalizeFetchError(e, "Nie udało się zaktualizować formy płatności dokumentu.");
  }
}

// -----------------------------
// ASSIGNMENT HISTORY
// -----------------------------

export async function getAssignmentHistory(
  companyId: string,
  documentId: string,
  page: number,
  pageSize: number
): Promise<AssignmentHistoryResponse> {
  const params = new URLSearchParams();
  params.set("companyId", companyId);
  params.set("page", page.toString());
  params.set("pageSize", pageSize.toString());

  const res = await apiFetch(
    `${API_URL}/documents/${documentId}/assignment-history?${params.toString()}`
  );

  if (!res.ok) {
    throw new Error(
      await getApiErrorMessage(res, "Nie udało się pobrać historii przypisań dokumentu.")
    );
  }

  return res.json();
}

export async function syncCompanies() {
  try {
    const res = await apiFetch(`${API_URL}/bc/companies/sync`, {
      method: "POST",
    });

    if (!res.ok) {
      throw new Error(
        await getApiErrorMessage(res, "Nie udało się zsynchronizować firm.")
      );
    }

    return res.json();
  } catch (e) {
    throw normalizeFetchError(e, "Nie udało się zsynchronizować firm.");
  }
}

export async function updateDocumentDescription(
  documentId: string,
  companyId: string,
  description: string
) {
  try {
    const res = await apiFetch(
      `${API_URL}/documents/${documentId}/description?companyId=${companyId}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ description }),
      }
    );

    if (!res.ok) {
      throw new Error(
        await getApiErrorMessage(res, "Nie udało się zaktualizować opisu dokumentu.")
      );
    }

    return res.json();
  } catch (e) {
    throw normalizeFetchError(e, "Nie udało się zaktualizować opisu dokumentu.");
  }
}

export function getKsefPreviewUrl(
  companyId: string,
  documentId: string
): string {
  return `${API_URL}/documents/${documentId}/ksef/preview?companyId=${companyId}`;
}